import classnames from 'classnames';
import axios, { AxiosError } from 'axios';
import { memoize } from 'lodash-es';
import React, { useCallback, useState } from 'react';
import { LayoutTitle, LayoutFooter } from './Layout';
import { toast } from 'react-toastify';
import { preventDefault, getErrorMessagesFromResponse } from '../../../common/utils';
import { BUSINESS_ACCOUNT_TYPE, PERSONAL_ACCOUNT_TYPE } from '../../../common/types';
import VerificationInput from 'react-verification-input';
import { Alert } from '../../../components/Alert';
import NotifyContainer from '../../../components/NotifyContainer';
import Button from '../../../components/Button';
import FormGroup from '../../../components/FormGroup';
import { updateHubspotContact } from '..';
export function Setup({ fields, updateFields, previousSection, nextSection, onLoading }) {
    const [initialSetup, setInitialSetup] = useState(true);
    const [emailSetup, setEmailSetup] = useState(false);
    const [accountDetailsSetup, setAccountDetailsSetup] = useState(false);
    const [emailVerificationSetup, setEmailVerificationSetup] = useState(false);
    const [title, setTitle] = useState('Choose your account');
    const [errors, setErrors] = useState([]);
    const [description, setDescription] = useState('Are you tracking from a business or personal account?');
    const goToInitialSetup = useCallback(() => {
        setTitle('Choose your account');
        setDescription('Are you tracking from a business or personal account?');
        setAccountDetailsSetup(false);
        setEmailSetup(false);
        setEmailVerificationSetup(false);
        setInitialSetup(true);
    }, [setTitle, setDescription, setInitialSetup, setAccountDetailsSetup, setEmailSetup, setEmailVerificationSetup]);
    const updateAccountType = useCallback(memoize((value) => () => {
        updateFields({ 'accountType': value });
    }), [updateFields]);
    const updateEmailVerified = useCallback(() => {
        updateFields({ ['emailVerified']: fields.email });
    }, [updateFields, fields]);
    const updateOtpFirstGenerated = useCallback(() => {
        updateFields({ ['otpFirstGenerated']: true });
    }, [updateFields]);
    const updateEmailVerificationCode = useCallback((value) => {
        updateFields({ ['emailVerificationCode']: value });
    }, [updateFields]);
    const updateField = useCallback(memoize((field) => (event) => {
        updateFields({ [field]: event.target.value });
    }), [updateFields]);
    const updatePrivacyPolicy = useCallback(() => {
        updateFields({ ['privacyAccepted']: !fields.privacyAccepted });
    }, [updateFields, fields]);
    const validateEmail = useCallback(preventDefault(async () => {
        try {
            onLoading(true);
            await axios.post('/api/register/validate-email', {
                email: fields.email,
            });
            setErrors([]);
            return true;
        }
        catch (error) {
            if (error instanceof AxiosError) {
                setErrors(getErrorMessagesFromResponse(error));
            }
            else if (error instanceof Error) {
                setErrors([error.message]);
            }
            else {
                setErrors(['Unknown Error']);
            }
            window.scrollTo(0, 300);
            throw error;
        }
        finally {
            onLoading(false);
        }
    }), [fields, setErrors, onLoading]);
    const validateAccountType = useCallback(preventDefault(async () => {
        try {
            onLoading(true);
            await axios.post('/api/register/validate-account-type', {
                account_type: fields.accountType,
            });
            setErrors([]);
            return true;
        }
        catch (error) {
            if (error instanceof AxiosError) {
                setErrors(getErrorMessagesFromResponse(error));
            }
            else if (error instanceof Error) {
                setErrors([error.message]);
            }
            else {
                setErrors(['Unknown Error']);
            }
            window.scrollTo(0, 300);
            throw error;
        }
        finally {
            onLoading(false);
        }
    }), [fields, setErrors, onLoading]);
    const validateAccount = useCallback(preventDefault(async () => {
        try {
            onLoading(true);
            await axios.post('/api/register/validate-account', {
                first_name: fields.firstName,
                last_name: fields.lastName,
                password: fields.password,
                password_confirmation: fields.password_confirmation,
                privacy_accepted: fields.privacyAccepted,
            });
            setErrors([]);
            return true;
        }
        catch (error) {
            if (error instanceof AxiosError) {
                setErrors(getErrorMessagesFromResponse(error));
            }
            else if (error instanceof Error) {
                setErrors([error.message]);
            }
            else {
                setErrors(['Unknown Error']);
            }
            window.scrollTo(0, 300);
            throw error;
        }
        finally {
            onLoading(false);
        }
    }), [fields, setErrors, onLoading]);
    const validateOtp = useCallback(preventDefault(async () => {
        try {
            onLoading(true);
            await axios.post('/api/register/validate-otp', {
                email: fields.email,
                otp: fields.emailVerificationCode,
            });
            setErrors([]);
            return true;
        }
        catch (error) {
            if (error instanceof AxiosError) {
                setErrors(getErrorMessagesFromResponse(error));
            }
            else if (error instanceof Error) {
                setErrors([error.message]);
            }
            else {
                setErrors(['Unknown Error']);
            }
            window.scrollTo(0, 300);
            throw error;
        }
        finally {
            onLoading(false);
        }
    }), [fields, setErrors, onLoading]);
    const sendOtp = useCallback(preventDefault(async () => {
        try {
            onLoading(true);
            await axios.post('/api/register/send-otp', {
                email: fields.email,
            });
            setErrors([]);
            updateEmailVerificationCode('');
            toast.success(`Code has been sent to ${fields.email}!`);
            return true;
        }
        catch (error) {
            if (error instanceof AxiosError) {
                setErrors(getErrorMessagesFromResponse(error));
            }
            else if (error instanceof Error) {
                setErrors([error.message]);
            }
            else {
                setErrors(['Unknown Error']);
            }
            window.scrollTo(0, 300);
            throw error;
        }
        finally {
            onLoading(false);
        }
    }), [fields, setErrors, onLoading]);
    const goToEmailSetup = useCallback(() => {
        setTitle(`Create a ${fields.accountType} account`);
        setDescription('Create your new account and get started on tracking.');
        setInitialSetup(false);
        setAccountDetailsSetup(false);
        setEmailVerificationSetup(false);
        setEmailSetup(true);
    }, [fields, setTitle, setDescription, setInitialSetup, setAccountDetailsSetup,
        setEmailSetup, setEmailVerificationSetup]);
    const goToAccountDetailsSetup = useCallback(() => {
        setTitle(`Create a ${fields.accountType} account`);
        setDescription(`Just a few more steps! Create your new account for
      <strong>${fields.email}</strong> completing these details.`);
        setInitialSetup(false);
        setEmailSetup(false);
        setEmailVerificationSetup(false);
        setAccountDetailsSetup(true);
    }, [fields, setTitle, setDescription, setInitialSetup, setAccountDetailsSetup,
        setEmailSetup, setEmailVerificationSetup]);
    const goToEmailVerificationSetup = useCallback(async () => {
        setTitle(`You're almost there!`);
        setDescription(`Please check your inbox for verification code sent to <strong>${fields.email}</strong>`);
        setInitialSetup(false);
        setEmailSetup(false);
        setAccountDetailsSetup(false);
        setEmailVerificationSetup(true);
        await sendOtp();
    }, [fields, setTitle, setDescription, setInitialSetup, setAccountDetailsSetup,
        setEmailSetup, setEmailVerificationSetup, sendOtp]);
    const previousSetupStep = useCallback(preventDefault(async () => {
        setErrors([]);
        window.scrollTo(0, 0);
        if (initialSetup) {
            previousSection();
        }
        if (emailSetup) {
            goToInitialSetup();
        }
        if (accountDetailsSetup) {
            goToEmailSetup();
        }
        if (emailVerificationSetup) {
            goToAccountDetailsSetup();
        }
    }), [fields, initialSetup, emailSetup, accountDetailsSetup, previousSection, emailVerificationSetup,
        goToInitialSetup, goToEmailSetup, goToAccountDetailsSetup, setErrors, updateOtpFirstGenerated]);
    const nextSetupStep = useCallback(preventDefault(async () => {
        onLoading(true);
        const { hsCompanyId, hsContactId } = await updateHubspotContact(fields);
        updateFields({ hsCompanyId, hsContactId });
        onLoading(false);
        setErrors([]);
        window.scrollTo(0, 0);
        if (initialSetup) {
            const accountTypeValidated = await validateAccountType();
            if (accountTypeValidated) {
                goToEmailSetup();
            }
        }
        if (emailSetup) {
            const emailValidated = await validateEmail();
            if (emailValidated) {
                goToAccountDetailsSetup();
            }
        }
        if (accountDetailsSetup) {
            const accountValidated = await validateAccount();
            if (accountValidated) {
                if (fields.emailVerified === fields.email) {
                    nextSection();
                }
                else {
                    goToEmailVerificationSetup();
                }
            }
        }
        if (emailVerificationSetup) {
            const otpValidated = await validateOtp();
            if (otpValidated) {
                updateEmailVerified();
                nextSection();
            }
        }
    }), [initialSetup, fields, emailSetup, accountDetailsSetup, emailVerificationSetup, validateEmail, validateAccount,
        goToEmailSetup, goToAccountDetailsSetup, goToEmailVerificationSetup, nextSection, setErrors, updateEmailVerified,
        validateOtp, updateFields, updateHubspotContact]);
    return (React.createElement("div", { className: 'register-setup' },
        React.createElement(NotifyContainer, null),
        React.createElement("div", { className: classnames('register-setup__img-wrapper', { 'register-setup__img-wrapper--displayed': emailVerificationSetup === true }) },
            React.createElement("img", { src: '/images/email-icon.png' })),
        React.createElement(LayoutTitle, { text: title }),
        React.createElement("p", { className: 'register-setup__description', dangerouslySetInnerHTML: { __html: description } }),
        React.createElement("div", { className: "register-setup__form-wrapper" },
            initialSetup && (React.createElement(React.Fragment, null,
                React.createElement(Alert, { messages: errors, type: "error" }),
                React.createElement("ul", { className: "account-toggle" },
                    React.createElement("li", null,
                        React.createElement("button", { type: "button", className: classnames('account-toggle__button', 'account-toggle__button--left', { 'account-toggle__button--selected': fields.accountType === BUSINESS_ACCOUNT_TYPE }), onClick: updateAccountType(BUSINESS_ACCOUNT_TYPE), "aria-current": fields.accountType === BUSINESS_ACCOUNT_TYPE },
                            "Business Account",
                            React.createElement("span", null, "(For Medical, Banking, Insurance...)"))),
                    React.createElement("li", null,
                        React.createElement("button", { type: "button", className: classnames('account-toggle__button', 'account-toggle__button--right', { 'account-toggle__button--selected': fields.accountType === PERSONAL_ACCOUNT_TYPE }), onClick: updateAccountType(PERSONAL_ACCOUNT_TYPE), "aria-current": fields.accountType === PERSONAL_ACCOUNT_TYPE },
                            "Personal Account",
                            React.createElement("span", null, "(For personal use)")))))),
            emailSetup && (React.createElement("div", { className: 'register-setup__emailSetup' },
                React.createElement(Alert, { messages: errors, type: "error" }),
                React.createElement("form", { onSubmit: nextSetupStep },
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                fields.accountType === BUSINESS_ACCOUNT_TYPE ? 'Company Email' : 'Email',
                                React.createElement("span", { className: "form__required" })),
                            React.createElement("input", { type: "email", className: "form__control", placeholder: "", maxLength: 100, required: true, value: fields.email, name: "email", onChange: updateField('email') }),
                            fields.emailVerified === fields.email && fields.emailVerified.length > 0 && (React.createElement("div", { className: classnames('alert', `alert--success`) },
                                React.createElement("div", null, "Email has been successfully verified!"))))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement("button", { type: 'submit', className: 'button button--marketing-solid button--expanded' }, "Continue"))))),
            accountDetailsSetup && (React.createElement("div", { className: 'register-setup__emailSetup' },
                React.createElement(Alert, { messages: errors, type: "error" }),
                React.createElement("form", { onSubmit: nextSetupStep },
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "First name",
                                React.createElement("span", { className: "form__required" })),
                            React.createElement("input", { type: "text", className: "form__control", placeholder: "", minLength: 2, maxLength: 50, value: fields.firstName, onChange: updateField('firstName'), name: "fname", required: true }))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "Last name",
                                React.createElement("span", { className: "form__required" })),
                            React.createElement("input", { type: "text", className: "form__control", placeholder: "", minLength: 2, maxLength: 50, name: "lname", required: true, value: fields.lastName, onChange: updateField('lastName') }))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "Create password",
                                React.createElement("span", { className: "form__required" })),
                            React.createElement("input", { type: "password", className: "form__control", autoComplete: "off", placeholder: "", minLength: 8, maxLength: 30, required: true, value: fields.password, onChange: updateField('password') }))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "Confirm password",
                                React.createElement("span", { className: "form__required" })),
                            React.createElement("input", { type: "password", className: "form__control", autoComplete: "off", placeholder: "", minLength: 8, maxLength: 30, required: true, value: fields.password_confirmation, onChange: updateField('password_confirmation') }))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement("div", { className: "checkbox-link" },
                            React.createElement("label", null,
                                React.createElement("input", { type: "checkbox", checked: fields.privacyAccepted, onChange: updatePrivacyPolicy, required: true }),
                                React.createElement("div", null,
                                    React.createElement("span", null, "By creating an account, I agree to the\u00A0"),
                                    React.createElement("span", { className: "checkbox-link__required" },
                                        React.createElement("a", { href: 'https://tracknotion.com/privacy-policy/', target: "_blank", rel: "noreferrer" }, "Privacy Policy")),
                                    "\u00A0and\u00A0",
                                    React.createElement("span", { className: "checkbox-link__required" },
                                        React.createElement("a", { href: 'https://tracknotion.com/terms/', target: "_blank", rel: "noreferrer" }, "Terms of Service")),
                                    React.createElement("span", { className: "form__required" }))))),
                    React.createElement(LayoutFooter, { positionShouldChange: true },
                        React.createElement(Button, { variant: "marketing-border", type: 'button', onClick: previousSetupStep }, "Go Back"),
                        React.createElement("input", { className: 'button button--marketing-solid', type: "submit", value: 'Create Account' }))))),
            emailVerificationSetup && (React.createElement("div", { className: "form__row" },
                React.createElement("div", { className: 'register-setup__verification-code' },
                    React.createElement(Alert, { messages: errors, type: "error" }),
                    React.createElement("span", null, "Enter code"),
                    React.createElement(VerificationInput, { length: 5, validChars: '0-9', value: fields.emailVerificationCode, placeholder: '', autoFocus: true, onChange: updateEmailVerificationCode, classNames: {
                            container: 'container',
                            character: 'form__control',
                            characterInactive: 'form__control',
                            characterSelected: 'form__control--focused',
                            characterFilled: 'form__control',
                        } }),
                    React.createElement("span", null, 'Can\'t find it? Please check your spam folder.'),
                    React.createElement("a", { href: '#', onClick: sendOtp }, "Resend code"))))),
        !accountDetailsSetup && (React.createElement(LayoutFooter, { positionShouldChange: true },
            React.createElement(Button, { variant: "marketing-border", type: 'button', onClick: previousSetupStep }, "Go Back"),
            initialSetup && (React.createElement(Button, { variant: "marketing-solid", onClick: nextSetupStep }, "Next")),
            emailVerificationSetup && (React.createElement(Button, { variant: "marketing-solid", onClick: nextSetupStep, disabled: fields.emailVerificationCode.length !== 5 }, "Verify Email"))))));
}
